import { Button, FieldError, FieldLabel, Input, PasswordField } from "@beweegbaas/ui";
import { createFormHook } from "@tanstack/react-form";
import { useHotkey } from "@tanstack/react-hotkeys";
import { useRef } from "react";
import { fieldErrorText, TanstackSubmitErrorSummary } from "#/components/form-errors";
import { passwordRequirements, passwordRules } from "#/modules/util.validation/schema.password";
import { fieldContext, formContext, useFieldContext, useFormContext } from "./form.context";
import MaterialSymbolsCheckSmall from "~icons/material-symbols/check-small";
import MaterialSymbolsCloseSmall from "~icons/material-symbols/close-small";

// ---------------------------------------------------------------------------
// Reusable field components
// ---------------------------------------------------------------------------

function TextField({ label, autoComplete }: { label: string; autoComplete?: string }) {
  const field = useFieldContext<string>();
  const hasError = field.state.meta.isTouched && !field.state.meta.isValid;

  return (
    <div className="flex flex-col gap-1">
      <FieldLabel>{label}</FieldLabel>
      <Input
        value={field.state.value}
        onChange={(e) => field.handleChange(e.target.value)}
        onBlur={field.handleBlur}
        aria-invalid={hasError || undefined}
        name={field.name}
        autoComplete={autoComplete}
      />
      {hasError && <FieldError>{fieldErrorText(field.state.meta.errors)}</FieldError>}
    </div>
  );
}

function PasswordTextField({
  label,
  showRequirements = false,
}: {
  label: string;
  showRequirements?: boolean;
}) {
  const field = useFieldContext<string>();
  const hasError = field.state.meta.isTouched && !field.state.meta.isValid;

  return (
    <div className="flex flex-col gap-1">
      <FieldLabel>{label}</FieldLabel>
      <PasswordField
        value={field.state.value}
        onChange={(e) => field.handleChange(e.target.value)}
        onBlur={field.handleBlur}
        aria-invalid={hasError || undefined}
        name={field.name}
        autoComplete={showRequirements ? "new-password" : "current-password"}
        passwordRules={showRequirements ? passwordRules : undefined}
      />
      {hasError && <FieldError>{fieldErrorText(field.state.meta.errors)}</FieldError>}
      {showRequirements && <PasswordRequirementsChecklist value={field.state.value} />}
    </div>
  );
}

/**
 * Live checklist of the Identity password policy so users see what's missing
 * while typing, instead of a generic failure after submit.
 */
function PasswordRequirementsChecklist({ value }: { value: string }) {
  return (
    <ul className="mt-1 space-y-0.5" aria-label="Wachtwoordeisen">
      {passwordRequirements.map((requirement) => {
        const met = requirement.test(value);
        return (
          <li
            key={requirement.key}
            className={`flex items-center gap-1.5 text-xs ${
              met ? "text-green-600 dark:text-green-400" : "text-muted-foreground"
            }`}
          >
            {met ? (
              <MaterialSymbolsCheckSmall className="h-4 w-4 shrink-0" />
            ) : (
              <MaterialSymbolsCloseSmall className="h-4 w-4 shrink-0" />
            )}
            {requirement.label}
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Reusable form components
// ---------------------------------------------------------------------------

function SubmitButton({ children, className }: { children: React.ReactNode; className?: string }) {
  const form = useFormContext();

  return (
    <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
      {([canSubmit, isSubmitting]) => (
        <Button
          type="submit"
          disabled={!canSubmit && !isSubmitting}
          isPending={isSubmitting}
          className={className}
        >
          {children}
        </Button>
      )}
    </form.Subscribe>
  );
}

/**
 * The standard form wrapper: noValidate + preventDefault + handleSubmit, with the
 * form-level error summary (errorMap.onSubmit) rendered above the fields.
 */
function FormRoot({
  children,
  className,
  submitOnModEnter = false,
}: {
  children: React.ReactNode;
  className?: string;
  /**
   * Cmd/Ctrl+Enter submits the form. Scoped to this form element, so it only fires while focus
   * is inside it — meant for composers whose main field is a multi-line editor where plain
   * Enter inserts a newline.
   */
  submitOnModEnter?: boolean;
}) {
  const form = useFormContext();
  const formRef = useRef<HTMLFormElement>(null);

  useHotkey("Mod+Enter", () => void form.handleSubmit(), {
    target: formRef,
    enabled: submitOnModEnter,
    preventDefault: true,
  });

  return (
    <form
      ref={formRef}
      noValidate
      className={className}
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        void form.handleSubmit();
      }}
    >
      <TanstackSubmitErrorSummary form={form} />
      {children}
    </form>
  );
}

// ---------------------------------------------------------------------------
// Hook factory
// ---------------------------------------------------------------------------

export const { useAppForm, withForm, withFieldGroup } = createFormHook({
  fieldComponents: {
    TextField,
    PasswordTextField,
  },
  formComponents: {
    SubmitButton,
    FormRoot,
  },
  fieldContext,
  formContext,
});

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

/**
 * Scroll to the first invalid form field after validation fails.
 * Uses react-aria's data-invalid attribute to find invalid fields.
 */
export const scrollToFirstError = () => {
  // Small delay to allow DOM to update with error states
  setTimeout(() => {
    const firstError = document.querySelector('[aria-invalid="true"]');
    if (firstError) {
      firstError.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, 0);
};
